import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container,
  Grid,
  Typography, 
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Button,
  alpha,
  Paper,
  Breadcrumbs,
  Link
} from "@material-ui/core";
import { Rating, Autocomplete } from "@mui/material";
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { questionnaireApi } from '../../api/questionnaireApi';
import { formationApi } from '../../api/formationApi';
import { reportsApi } from '../../api/reportsApi';
import SatisfactionReportPDF from '../../components/dashboard/formation/SatisfactionReportPDF';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import useSettings from '../../hooks/useSettings';
import Scrollbar from '../../components/Scrollbar';
import moment from 'moment';
import 'moment/locale/fr';
import toast from 'react-hot-toast';
import ChartPieIcon from '../../icons/ChartPie';
import UsersIcon from '../../icons/Users';
import TrashIcon from '../../icons/Trash';
import ChartSquareBarIcon from '../../icons/ChartSquareBar';
import DownloadIcon from '../../icons/Download';
import ChevronRightIcon from '../../icons/ChevronRight';
import StarIcon from '@material-ui/icons/Star';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import FeedIcon from '@mui/icons-material/Feed';

// --- EXTRAORDINARY STYLING COMPONENTS ---

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: '#F8FAFC',
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 150, -80, 0], y: [0, -150, 80, 0], scale: [1, 1.4, 0.7, 1] }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-20%', left: '-15%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(70px)' }}
        />
        <motion.div
            animate={{ x: [0, -120, 100, 0], y: [0, 120, -60, 0], scale: [1, 1.2, 0.85, 1] }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-30%', right: '-15%', width: '60vw', height: '60vw', background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(70px)' }}
        />
    </Box>
);

const QuestionnaireSatisfaction = () => {
  const { settings } = useSettings();
  const [currentTab, setCurrentTab] = useState('Satisfaction');
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedFormation, setSelectedFormation] = useState('All');
  const [selectedSession, setSelectedSession] = useState('All');
  const [submissions, setSubmissions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const tabs = [
    { label: 'Pré-Formation', value: 'Pré-Formation' },
    { label: 'Post-Formation', value: 'Post-Formation' },
    { label: 'Satisfaction', value: 'Satisfaction' }
  ];

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
  };

  const loadFormations = useCallback(async () => {
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const data = await formationApi.getFormations(accessToken);
      setFormations(data || []);
    } catch (error) { console.error(error); }
  }, []);

  const loadSessions = useCallback(async (formationId) => {
    if (formationId === 'All') {
      setSessions([]);
      setSelectedSession('All');
      return;
    }
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const data = await questionnaireApi.getSessionsByFormationId(formationId, accessToken);
      setSessions(data || []);
      setSelectedSession('All');
    } catch (error) { console.error(error); }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const fId = selectedFormation === 'All' ? null : selectedFormation;
      const sId = selectedSession === 'All' ? null : selectedSession;
      
      const response = await questionnaireApi.fetchSubmissionsByFilter(currentTab, fId, sId, accessToken);
      if (response && response.status) {
        setSubmissions(response.data || []);
        setQuestions(response.questions || []);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [currentTab, selectedFormation, selectedSession]);

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce résultat ?")) return;
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await questionnaireApi.deleteSubmission(id, accessToken);
      if (response.status) {
        toast.success("Résultat supprimé");
        loadSubmissions();
      }
    } catch (err) { toast.error("Erreur technique"); }
  };

  useEffect(() => { loadFormations(); }, [loadFormations]);
  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const handleFormationChange = (e) => {
    const value = e.target.value;
    setSelectedFormation(value);
    loadSessions(value);
  };

  const handleDownloadReport = async () => {
    const toastId = 'report-toast';
    try {
      setReportLoading(true);
      toast.loading("Préparation du rapport...", { id: toastId });
      const currentFormation = formations.find(f => f.id === selectedFormation);
      const currentSession = sessions.find(s => s.id === selectedSession);
      if (!currentFormation || !currentSession) throw new Error("Sélectionnez une formation et une session.");

      const { hostname } = window.location;
      const tenant = hostname.split('.')[0].toUpperCase();
      const accessToken = window.localStorage.getItem('accessToken');
      const learnersResponse = await reportsApi.getSatisfactionReportData(selectedSession, accessToken);
      
      const data = {
        formationName: currentFormation.name || 'Formation inconnue',
        sessionNumber: currentSession.number || 'N/A',
        location: currentSession.meeting_place || 'Non spécifié',
        date: currentSession.start_date ? moment(currentSession.start_date).format('DD MMMM YYYY') : 'Date inconnue',
        organism: currentFormation.organism || tenant || 'Apteed',
        participantsCount: learnersResponse?.data?.learners_status?.length || 0,
        speciality: currentFormation.specialite || '',
        learners: learnersResponse?.data?.learners_status || [],
        preFormation: learnersResponse?.data?.pre_formation || null,
        postFormation: learnersResponse?.data?.post_formation || null,
        progression: learnersResponse?.data?.progression || null,
        globalSatisfaction: learnersResponse?.data?.global_satisfaction_percent || 0,
        satisfaction_details: learnersResponse?.data?.satisfaction_details || null
      };
      
      const blob = await pdf(<SatisfactionReportPDF data={data} />).toBlob();
      saveAs(blob, `Rapport_Session_${data.sessionNumber}.pdf`);
      toast.success("Rapport téléchargé", { id: toastId });
    } catch (error) { toast.error(error.message, { id: toastId }); }
    finally { setReportLoading(false); }
  };

  const uniqueQuestions = [];
  const seenTexts = new Set();
  const identityLabels = ['nom', 'prénom', 'prenom', 'nom et prénom', 'nom et prenom', 'nom & prénom'];
  
  questions.forEach(q => {
    const lowerText = q.text.toLowerCase().trim();
    const isIdentity = identityLabels.some(label => lowerText === label || lowerText.startsWith(label + ' ') || lowerText.endsWith(':') && lowerText.slice(0,-1).trim() === label);
    if (!seenTexts.has(q.text) && !isIdentity) {
      seenTexts.add(q.text);
      uniqueQuestions.push(q);
    }
  });

  const globalAvg = submissions.length > 0 ? (() => {
    const totalScore = submissions.reduce((acc, sub) => acc + (parseFloat(sub.score) || 0), 0);
    const totalPossible = submissions.reduce((acc, sub) => acc + (parseInt(sub.total_points) || 0), 0);
    return totalPossible > 0 ? (totalScore / totalPossible) * 100 : 0;
  })() : 0;

  return (
    <>
      <Helmet>
        <title>Qualité | Résultats | Apteed</title>
      </Helmet>
      
      <Box sx={{ 
          bgcolor: '#F8FAFC', minHeight: '100%', position: 'relative', overflow: 'hidden', pb: 10, pt: 4
      }}>
        <AnimatedMeshBackground />

        <Container maxWidth={settings.compact ? 'xl' : false} sx={{ position: 'relative', zIndex: 1 }}>
          
          <Box sx={{ mb: 2 }}>
            <Button component={RouterLink} to="/dashboard" startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />} size="small" sx={{ color: '#64748B', fontWeight: 800, textTransform: 'none', bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '10px', px: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}>Retour</Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <FeedIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
                  </Box>
                  <Box>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
                        <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: '#6366f1' } }}>Dashboard</Link>
                        <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Qualité</Typography>
                    </Breadcrumbs>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F172A', letterSpacing: '-0.02em', mb: 0.5 }}>
                        Analytique Qualité
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                        Suivi détaillé des évaluations et de la satisfaction apprenants.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button color="primary" component={RouterLink} to="/dashboard/questionnaire-progression" startIcon={<ChartSquareBarIcon fontSize="small" />} variant="outlined" sx={{ borderRadius: '14px', px: 2.5, fontWeight: 800, textTransform: 'none' }}>Visual. Pré/Post</Button>
                    <Button color="secondary" component={RouterLink} to="/dashboard/visualisation-globale" startIcon={<ChartSquareBarIcon fontSize="small" />} variant="contained" sx={{ borderRadius: '14px', px: 2.5, fontWeight: 800, textTransform: 'none', boxShadow: '0 8px 16px -4px rgba(15, 23, 42, 0.2)' }}>Vue Globale</Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
                <Paper sx={{ p: 3, borderRadius: '24px', height: '100%', background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)', color: '#FFFFFF', position: 'relative', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.2)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, rgba(0,0,0,0) 70%)', borderRadius: '50%' }} />
                    
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>{globalAvg.toFixed(1)}%</Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Moyenne</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>{submissions.length}</Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Réponses</Typography>
                    </Box>
                </Paper>
            </Grid>
          </Grid>

          {/* Filtering Section */}
          <Paper sx={{ p: 1.5, borderRadius: '24px', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', border: '1px solid #FFFFFF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                    <Tabs value={currentTab} onChange={handleTabsChange} TabIndicatorProps={{ sx: { display: 'none' } }} sx={{ minHeight: 'auto' }}>
                        {tabs.map((tab) => (
                            <Tab key={tab.value} label={tab.label} value={tab.value} sx={{ minHeight: 'auto', py: 1, px: 2.5, borderRadius: '16px', fontWeight: 800, fontSize: '0.75rem', color: '#64748B', textTransform: 'none', transition: 'all 0.3s', '&.Mui-selected': { bgcolor: '#FFFFFF', color: '#6366F1', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' } }} />
                        ))}
                    </Tabs>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                        <Autocomplete
                            options={[{ id: 'All', name: 'Toutes les formations', programNumberAll: '' }, ...formations]}
                            getOptionLabel={(option) => {
                              if (!option) return "";
                              if (option.id === 'All') return "Toutes les formations";
                              return `${option.name} ${option.programNumberAll ? `(N° ${option.programNumberAll})` : ''}`.trim();
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={
                                selectedFormation === 'All'
                                    ? null
                                    : formations.find(f => f.id === selectedFormation) || null
                            }
                            onChange={(event, newValue) => {
                                handleFormationChange({ target: { value: newValue ? newValue.id : 'All' } });
                            }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Formation (Mots clés ou N° Prog)" 
                                    variant="outlined" 
                                    size="small" 
                                    InputProps={{ ...params.InputProps, style: { borderRadius: '12px', backgroundColor: 'white' } }} 
                                />
                            )}
                            style={{ minWidth: 280 }}
                        />
                        <TextField select label="Session" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} variant="outlined" size="small" sx={{ minWidth: 150 }} disabled={selectedFormation === 'All'} InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}>
                            <MenuItem value="All">Toutes les sessions</MenuItem>
                            {sessions.map((s) => ( <MenuItem key={s.id} value={s.id}>Session N° {s.number}</MenuItem> ))}
                        </TextField>
                        {selectedFormation !== 'All' && selectedSession !== 'All' && (
                            <Button color="primary" variant="contained" startIcon={reportLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon fontSize="small" />} onClick={handleDownloadReport} disabled={reportLoading} sx={{ borderRadius: '12px', fontWeight: 800, px: 2, textTransform: 'none' }}>Rapport PDF</Button>
                        )}
                    </Box>
                </Grid>
            </Grid>
          </Paper>

          {/* Table Card */}
          <Card sx={{ borderRadius: '24px', border: '1px solid rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)', overflow: 'hidden' }}>
            <Scrollbar>
              <Box sx={{ minWidth: 1200 }}>
                {loading ? (
                  <Box sx={{ p: 10, textAlign: 'center' }}><CircularProgress /></Box>
                ) : submissions.length > 0 ? (
                  <Table>
                    <TableHead sx={{ bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>Apprenant</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>Score</TableCell>
                        {currentTab === 'Satisfaction' && (
                          <TableCell align="center" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>Évaluation</TableCell>
                        )}
                        {uniqueQuestions.map((q, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', minWidth: 200 }}>{q.text}</TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <AnimatePresence mode="popLayout">
                        {submissions.map((sub) => {
                            const parsedAnswers = typeof sub.answers === 'string' ? JSON.parse(sub.answers) : (sub.answers || {});
                            const dateObj = moment(sub.created_at);
                            const scoreRatio = sub.total_points > 0 ? (sub.score / sub.total_points) : 0;
                            const ratingValue = (scoreRatio * 5);
                            
                            let sColor = '#EF4444';
                            if (scoreRatio >= 0.9) sColor = '#10B981';
                            else if (scoreRatio >= 0.75) sColor = '#6366F1';
                            else if (scoreRatio >= 0.5) sColor = '#F59E0B';

                            return (
                                <TableRow component={motion.tr} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} hover key={sub.id} sx={{ '&:hover': { bgcolor: 'rgba(99,102,241,0.02) !important' } }}>
                                    <TableCell sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 600 }}>{dateObj.isValid() ? dateObj.format('DD/MM/YYYY HH:mm') : '-'}</TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 800, color: '#1E293B', fontSize: '0.9rem' }}>{sub.lastname} {sub.firstname}</Typography>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>{sub.formation_name}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: alpha(sColor, 0.1), color: sColor, display: 'inline-flex', fontWeight: 900, fontSize: '0.75rem' }}>
                                            {sub.total_points > 0 ? `${(scoreRatio * 100).toFixed(1)}%` : '-'}
                                        </Box>
                                    </TableCell>
                                    {currentTab === 'Satisfaction' && (
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <StarIcon sx={{ fontSize: 16, color: '#FBBF24' }} />
                                                <Typography sx={{ fontWeight: 900, color: '#1E293B', fontSize: '0.85rem' }}>{ratingValue.toFixed(1)}</Typography>
                                            </Box>
                                        </TableCell>
                                    )}
                                    {uniqueQuestions.map((uq, idx) => {
                                        const relatedQuestion = questions.find(q => q.questionnaire_id === sub.questionnaire_id && q.text === uq.text);
                                        const answer = relatedQuestion ? parsedAnswers[relatedQuestion.id] : '-';
                                        return (
                                            <TableCell key={idx}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#475569', maxWidth: 300 }}>
                                                    {typeof answer === 'object' ? (answer.text || JSON.stringify(answer)) : (answer || '-')}
                                                </Typography>
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="center">
                                        <Tooltip title="Supprimer">
                                            <IconButton onClick={() => handleDeleteSubmission(sub.id)} size="small" sx={{ color: '#EF4444' }}>
                                                <TrashIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                      </AnimatePresence>
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 10, opacity: 0.5 }}>
                    <ChartPieIcon sx={{ fontSize: 48, mb: 2, color: '#94A3B8' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#94A3B8' }}>Aucun résultat pour "{currentTab}"</Typography>
                  </Box>
                )}
              </Box>
            </Scrollbar>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default QuestionnaireSatisfaction;
